import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productApi, netValueApi } from '../../services/privateFundApi';
import './NetValueUpdate.css';

/**
 * 净值更新页面
 */
const NetValueUpdate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [formData, setFormData] = useState({
    netValue: '',
    netValueDate: new Date().toISOString().split('T')[0]
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [showAddInvestorDialog, setShowAddInvestorDialog] = useState(false);
  const [investorList, setInvestorList] = useState([]);
  const [newInvestor, setNewInvestor] = useState({
    name: '',
    amount: ''
  });

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await productApi.getProductById(id);
      if (response.success) {
        setProduct(response.data);
      }
    } catch (err) {
      console.error('获取产品失败:', err);
      setError(err.message || '获取产品失败');
    }
  };

  // 计算预览盈亏
  useEffect(() => {
    if (product && formData.netValue) {
      const netValue = parseFloat(formData.netValue);
      if (netValue > 0 && product.purchaseNetValue) {
        const profitLoss = ((netValue - product.purchaseNetValue) / product.purchaseNetValue) * product.purchaseAmount;
        const profitLossRatio = profitLoss / product.purchaseAmount;
        setPreview({
          profitLoss,
          profitLossRatio
        });
      } else {
        setPreview(null);
      }
    } else {
      setPreview(null);
    }
  }, [formData.netValue, product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // 保存更新前的净值，用于计算变化
      const previousNetValue = product.latestNetValue;
      const newNetValue = parseFloat(formData.netValue);

      const response = await netValueApi.updateNetValue(id, {
        netValue: newNetValue,
        netValueDate: formData.netValueDate
      });

      if (response.success) {
        // 重新获取产品详情以获取最新的投资人员盈亏信息
        const updatedProductRes = await productApi.getProductById(id);
        
        if (!updatedProductRes.success) {
          setError('获取更新后的产品信息失败');
          return;
        }
        
        const updatedProduct = updatedProductRes.data;
        
        // 计算总金额（最新净值 * 申购份额）
        const totalAmount = newNetValue * product.purchaseShares;
        
        // 计算与上次净值的比较（如果有上次净值）
        let comparisonData = null;
        if (previousNetValue && previousNetValue > 0) {
          const netValueChange = newNetValue - previousNetValue;
          const returnRatio = (netValueChange / previousNetValue) * 100;
          const profitLossAmount = netValueChange * product.purchaseShares;
          
          comparisonData = {
            previousNetValue,
            netValueChange,
            returnRatio,
            profitLossAmount
          };
        }
        
        // 获取个人盈亏信息
        const investorProfitLoss = (updatedProduct.productInvestors || []).map(pi => ({
          investorName: pi.investorId?.name || '未知',
          investmentAmount: pi.investmentAmount || 0,
          investmentRatio: pi.investmentRatio || 0,
          profitLoss: pi.profitLoss || 0,
          profitLossRatio: pi.profitLossRatio || 0
        }));
        
        // 设置成功数据并显示对话框
        setSuccessData({
          totalAmount,
          newNetValue,
          comparisonData,
          profitLoss: response.data.profitLoss,
          profitLossRatio: response.data.profitLossRatio,
          investorProfitLoss
        });
        // 初始化投资人列表
        setInvestorList(investorProfitLoss.map(inv => ({
          name: inv.investorName,
          amount: inv.investmentAmount
        })));
        setShowSuccessDialog(true);
      } else {
        setError(response.message || '更新净值失败');
      }
    } catch (err) {
      console.error('更新净值失败:', err);
      setError(err.message || '更新净值失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/private-fund/products/${id}`);
  };

  const handleCloseDialog = () => {
    setShowSuccessDialog(false);
    setInvestorList([]);
    navigate(`/private-fund/products/${id}`);
  };

  const handleAddInvestor = () => {
    setShowAddInvestorDialog(true);
    setNewInvestor({ name: '', amount: '' });
  };

  const handleAddInvestorSubmit = (e) => {
    e.preventDefault();
    
    if (!newInvestor.name.trim()) {
      setError('请输入投资人姓名');
      return;
    }
    
    const amount = parseFloat(newInvestor.amount);
    if (!amount || amount <= 0) {
      setError('请输入有效的投资金额');
      return;
    }
    
    // 添加到列表
    setInvestorList(prev => [...prev, {
      name: newInvestor.name.trim(),
      amount: amount
    }]);
    
    // 关闭对话框并清空表单
    setShowAddInvestorDialog(false);
    setNewInvestor({ name: '', amount: '' });
    setError(null);
  };

  const handleRemoveInvestor = (index) => {
    setInvestorList(prev => prev.filter((_, i) => i !== index));
  };

  const handleCancelAddInvestor = () => {
    setShowAddInvestorDialog(false);
    setNewInvestor({ name: '', amount: '' });
    setError(null);
  };

  if (!product) {
    return (
      <div className="net-value-update">
        <div className="loading-message">加载中...</div>
      </div>
    );
  }

  return (
    <div className="net-value-update">
      <div className="update-header">
        <button className="back-button" onClick={handleCancel}>
          ← 返回
        </button>
        <h1 className="update-title">更新最新净值</h1>
      </div>

      <div className="update-content">
        <div className="product-info-card">
          <h3>{product.name}</h3>
          <div className="info-row">
            <span>申购金额:</span>
            <span>{product.purchaseAmount.toLocaleString()} 元</span>
          </div>
          <div className="info-row">
            <span>申购净值:</span>
            <span>{product.purchaseNetValue}</span>
          </div>
          {product.latestNetValue && (
            <div className="info-row">
              <span>当前最新净值:</span>
              <span>{product.latestNetValue}</span>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="netValueDate">净值日期 *</label>
            <input
              type="date"
              id="netValueDate"
              name="netValueDate"
              value={formData.netValueDate}
              onChange={handleChange}
              required
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="form-group">
            <label htmlFor="netValue">最新净值 *</label>
            <input
              type="number"
              id="netValue"
              name="netValue"
              value={formData.netValue}
              onChange={handleChange}
              required
              min="0.01"
              step="0.0001"
              placeholder="请输入最新净值"
            />
          </div>

          {preview && (
            <div className="preview-card">
              <h3>盈亏预览</h3>
              <div className="preview-item">
                <span>盈亏金额:</span>
                <span className={preview.profitLoss >= 0 ? 'profit' : 'loss'}>
                  {preview.profitLoss >= 0 ? '+' : ''}{preview.profitLoss.toFixed(2)} 元
                </span>
              </div>
              <div className="preview-item">
                <span>盈亏比例:</span>
                <span className={preview.profitLossRatio >= 0 ? 'profit' : 'loss'}>
                  {preview.profitLossRatio >= 0 ? '+' : ''}{(preview.profitLossRatio * 100).toFixed(2)}%
                </span>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleCancel}>
              取消
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? '更新中...' : '更新净值'}
            </button>
          </div>
        </form>
      </div>

      {/* 成功对话框 */}
      {showSuccessDialog && successData && (
        <div className="success-dialog-overlay" onClick={handleCloseDialog}>
          <div className="success-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="success-dialog-header">
              <h2>✓ 净值更新成功</h2>
            </div>
            <div className="success-dialog-content">
              <div className="success-info-section">
                <h3>更新后总金额</h3>
                <div className="success-value">
                  {successData.totalAmount.toLocaleString('zh-CN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} 元
                </div>
                <div className="success-detail">
                  <span>最新净值: {successData.newNetValue}</span>
                  <span>申购份额: {product.purchaseShares.toLocaleString('zh-CN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</span>
                </div>
              </div>

              {successData.comparisonData && (
                <div className="success-info-section">
                  <h3>与上次净值对比</h3>
                  <div className="comparison-row">
                    <span>上次净值:</span>
                    <span>{successData.comparisonData.previousNetValue}</span>
                  </div>
                  <div className="comparison-row">
                    <span>净值变化:</span>
                    <span className={successData.comparisonData.netValueChange >= 0 ? 'profit' : 'loss'}>
                      {successData.comparisonData.netValueChange >= 0 ? '+' : ''}
                      {successData.comparisonData.netValueChange.toFixed(4)}
                    </span>
                  </div>
                  <div className="comparison-row">
                    <span>收益率:</span>
                    <span className={successData.comparisonData.returnRatio >= 0 ? 'profit' : 'loss'}>
                      {successData.comparisonData.returnRatio >= 0 ? '+' : ''}
                      {successData.comparisonData.returnRatio.toFixed(2)}%
                    </span>
                  </div>
                  <div className="comparison-row">
                    <span>盈亏金额:</span>
                    <span className={successData.comparisonData.profitLossAmount >= 0 ? 'profit' : 'loss'}>
                      {successData.comparisonData.profitLossAmount >= 0 ? '+' : ''}
                      {successData.comparisonData.profitLossAmount.toLocaleString('zh-CN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })} 元
                    </span>
                  </div>
                </div>
              )}

              <div className="success-info-section">
                <h3>累计盈亏（相对申购净值）</h3>
                <div className="comparison-row">
                  <span>盈亏金额:</span>
                  <span className={successData.profitLoss >= 0 ? 'profit' : 'loss'}>
                    {successData.profitLoss >= 0 ? '+' : ''}
                    {successData.profitLoss.toLocaleString('zh-CN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} 元
                  </span>
                </div>
                <div className="comparison-row">
                  <span>盈亏比例:</span>
                  <span className={successData.profitLossRatio >= 0 ? 'profit' : 'loss'}>
                    {successData.profitLossRatio >= 0 ? '+' : ''}
                    {(successData.profitLossRatio * 100).toFixed(2)}%
                  </span>
                </div>
              </div>

              <div className="success-info-section">
                <div className="section-header-with-button">
                  <h3>投资人情况</h3>
                  <button 
                    type="button" 
                    className="add-investor-button"
                    onClick={handleAddInvestor}
                  >
                    + 添加投资人
                  </button>
                </div>
                {investorList.length > 0 ? (
                  <div className="investor-profit-list">
                    {investorList.map((investor, index) => (
                      <div key={index} className="investor-profit-item">
                        <div className="investor-detail-row">
                          <span className="investor-name">{investor.name}</span>
                          <div className="investor-actions">
                            <span className="investor-amount">
                              {investor.amount.toLocaleString('zh-CN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })} 元
                            </span>
                            <button
                              type="button"
                              className="remove-investor-button"
                              onClick={() => handleRemoveInvestor(index)}
                              title="删除"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-investor-list">
                    <p>暂无投资人，点击"添加投资人"按钮添加</p>
                  </div>
                )}
              </div>
            </div>
            <div className="success-dialog-footer">
              <button className="confirm-button" onClick={handleCloseDialog}>
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 添加投资人对话框 */}
      {showAddInvestorDialog && (
        <div className="add-investor-dialog-overlay" onClick={handleCancelAddInvestor}>
          <div className="add-investor-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="add-investor-dialog-header">
              <h3>添加投资人</h3>
              <button className="close-button" onClick={handleCancelAddInvestor}>×</button>
            </div>
            <form onSubmit={handleAddInvestorSubmit} className="add-investor-form">
              {error && (
                <div className="error-message">
                  <p>{error}</p>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="investorName">姓名 *</label>
                <input
                  type="text"
                  id="investorName"
                  value={newInvestor.name}
                  onChange={(e) => setNewInvestor({ ...newInvestor, name: e.target.value })}
                  placeholder="请输入投资人姓名"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="investorAmount">投资金额（元）*</label>
                <input
                  type="number"
                  id="investorAmount"
                  value={newInvestor.amount}
                  onChange={(e) => setNewInvestor({ ...newInvestor, amount: e.target.value })}
                  placeholder="请输入投资金额"
                  min="0.01"
                  step="0.01"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleCancelAddInvestor}>
                  取消
                </button>
                <button type="submit" className="submit-button">
                  添加
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetValueUpdate;


